import { BellIcon, WarningIcon, Mtds } from 'prove-common'
import { type ReactNode, useId, useState } from 'react'
import { Link } from 'react-router'
import { useAllSamples } from '../../hooks/useAllSamples'
import { useGetRequisitionsForLabQuery } from '../../hooks/useGetRequisitionsForLab'
import {
  getProcessingErrorCopy,
  normalizeProcessingErrorText
} from '../../utils/processingErrorUtils'

const NotificationItem = ({
  sampleId,
  latestProcessingError,
  locale,
  isProvePlan
}: {
  sampleId: string
  latestProcessingError?: string | null
  locale: 'nb' | 'en'
  isProvePlan: boolean
}) => {
  const copy = getProcessingErrorCopy(locale)
  const [showRawText, setShowRawText] = useState(false)
  const message = normalizeProcessingErrorText(latestProcessingError, locale)
  const disclosureLabel = showRawText
    ? copy.hideDisclosureLabel
    : copy.disclosureLabel

  if (!isProvePlan) {
    return (
      <li key={sampleId} className='py-2'>
        <Mtds.Prose data-color='warning'>
          <p className='mb-1 flex items-center gap-2 font-bold'>
            <WarningIcon color='var(--mtds-color-base-default)' weight='fill' />
            <span>{sampleId}</span>
          </p>
          <Mtds.Tag data-color='warning'>Processing error</Mtds.Tag>
          {message && (
            <p className='mt-1 whitespace-pre-wrap text-sm leading-snug'>
              {message}
            </p>
          )}
        </Mtds.Prose>
      </li>
    )
  }

  return (
    <li key={sampleId} className='py-2'>
      <Mtds.Prose data-color='warning'>
        <p className='mb-1 flex items-center gap-2 font-bold'>
          <WarningIcon color='var(--mtds-color-base-default)' weight='fill' />
          <Link
            to={`/samples/${sampleId}`}
            className='no-underline hover:no-underline focus:no-underline'
          >
            {sampleId}
          </Link>
        </p>
        <p className='text-sm leading-snug'>{copy.explanation}</p>
        {showRawText && (
          <p className='mt-1 whitespace-pre-wrap text-sm leading-snug'>
            {message}
          </p>
        )}
        <Mtds.Button
          data-variant='tertiary'
          data-size='sm'
          onClick={() => setShowRawText((current) => !current)}
          aria-expanded={showRawText}
          aria-label={disclosureLabel}
        >
          {disclosureLabel}
        </Mtds.Button>
      </Mtds.Prose>
    </li>
  )
}

type NotificationPopoverProps = {
  isProvePlan?: boolean
}

export const NotificationPopover = ({
  isProvePlan = true
}: NotificationPopoverProps) => {
  const popId = useId()
  const [hasBeenOpened, setHasBeenOpened] = useState(false)

  const { samples, isLoading: isSamplesLoading } = useAllSamples({
    isEnabled: isProvePlan && hasBeenOpened,
    includeEvents: true,
    filters: {
      planIds: [],
      productIds: [],
      matrixIds: [],
      sampntTypeIds: [],
      substanceIds: [],
      status: []
    }
  })

  const { data: requisitions, isLoading: isRequisitionsLoading } =
    useGetRequisitionsForLabQuery({
      isEnabled: !isProvePlan && hasBeenOpened
    })

  const isLoading = isProvePlan ? isSamplesLoading : isRequisitionsLoading

  const samplesWithProcessingError = samples
    .filter((sample) => {
      return (
        (sample.requisitions?.length ?? 0) > 0 &&
        sample.requisitions?.some((req) => req.latestProcessingError != null)
      )
    })
    .sort((a, b) => a.id.localeCompare(b.id))

  const processingErrorByKey = Object.fromEntries(
    samplesWithProcessingError.flatMap((sample) =>
      (sample.requisitions ?? [])
        .filter((req) => req.latestProcessingError != null)
        .map((req) => [
          `${sample.id}-${req.id}`,
          {
            sampleId: sample.id,
            requisitionId: req.id,
            latestProcessingError: req.latestProcessingError
          }
        ])
    )
  )

  const requisitionsWithProcessingError = (requisitions ?? [])
    .filter((req) => req.latestProcessingError != null && req.sampleId)
    .sort((a, b) => a.sampleId.localeCompare(b.sampleId))

  const requisitionProcessingErrorByKey = Object.fromEntries(
    requisitionsWithProcessingError.map((req) => [
      `${req.sampleId}-${req.id}`,
      {
        sampleId: req.sampleId,
        requisitionId: req.id,
        latestProcessingError: req.latestProcessingError
      }
    ])
  )

  const sampleIdAndLatestProcessingError = Object.values(
    isProvePlan ? processingErrorByKey : requisitionProcessingErrorByKey
  ).map((item) => ({
    sampleId: item.sampleId,
    latestProcessingError: item.latestProcessingError
  }))

  const locale = isProvePlan ? 'nb' : 'en'
  const copy = getProcessingErrorCopy(locale)
  const hasItems = sampleIdAndLatestProcessingError.length > 0
  let popoverContent: ReactNode = copy.emptyState

  if (isLoading) {
    popoverContent = <Mtds.Spinner aria-label={copy.loadingState} />
  } else if (hasItems) {
    popoverContent = (
      <ul className='space-y-0 divide-y divide-neutral-500'>
        {sampleIdAndLatestProcessingError.map((item) => (
          <NotificationItem
            key={item.sampleId}
            sampleId={item.sampleId}
            latestProcessingError={item.latestProcessingError}
            locale={locale}
            isProvePlan={isProvePlan}
          />
        ))}
      </ul>
    )
  }

  return (
    <>
      <Mtds.Button
        data-tooltip={copy.notificationButtonLabel}
        popoverTarget={popId}
        aria-label={copy.notificationButtonLabel}
        onClick={() => setHasBeenOpened(true)}
      >
        <span className='relative inline-flex items-center justify-center'>
          <BellIcon />
          {hasItems && (
            <span
              className='absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-white'
              aria-hidden='true'
            />
          )}
        </span>
      </Mtds.Button>
      <Mtds.Popover id={popId} className='space-y-2'>
        <Mtds.Heading>{copy.notificationsTitle}</Mtds.Heading>
        <Mtds.Divider className='border-b border-b-neutral-500' />
        {popoverContent}
      </Mtds.Popover>
    </>
  )
}
